import * as React from 'react';
import { createPortal } from 'react-dom';
import { Pipette } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Label } from './label';
import { Icon } from './Icon';
import { appLayerProps } from './app-layer';

/* ================================================================
 * Color math — pure, dependency-free, testable in isolation.
 * r/g/b are 0-255 ints, a is 0-1 float, h is 0-360, s/v are 0-100.
 * ================================================================ */

export interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface HSV {
  h: number;
  s: number;
  v: number;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

/** Parses `#rgb`, `#rgba`, `#rrggbb`, `#rrggbbaa` (leading `#` optional). */
export function parseHex(input: string): RGBA | null {
  const hex = input.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
  let r: number;
  let g: number;
  let b: number;
  let a = 1;
  if (hex.length === 3 || hex.length === 4) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
    if (hex.length === 4) a = parseInt(hex[3] + hex[3], 16) / 255;
  } else if (hex.length === 6 || hex.length === 8) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
    if (hex.length === 8) a = parseInt(hex.slice(6, 8), 16) / 255;
  } else {
    return null;
  }
  if ([r, g, b, a].some((n) => Number.isNaN(n))) return null;
  return { r, g, b, a };
}

/** Parses `#hex(6/8/3/4)`, `rgb(r,g,b)`, or `rgba(r,g,b,a)`. */
export function parseColor(input: string): RGBA | null {
  if (!input) return null;
  const s = input.trim();
  const rgbMatch = s.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i
  );
  if (rgbMatch) {
    const r = clamp(Math.round(parseFloat(rgbMatch[1])), 0, 255);
    const g = clamp(Math.round(parseFloat(rgbMatch[2])), 0, 255);
    const b = clamp(Math.round(parseFloat(rgbMatch[3])), 0, 255);
    const a = rgbMatch[4] !== undefined ? clamp(parseFloat(rgbMatch[4]), 0, 1) : 1;
    if ([r, g, b, a].some((n) => Number.isNaN(n))) return null;
    return { r, g, b, a };
  }
  return parseHex(s);
}

/** `#rrggbb` when alpha is opaque (or alpha is disallowed), else `#rrggbbaa`. */
export function formatHex(rgba: RGBA, allowAlpha = true): string {
  const toHex = (n: number) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, '0');
  const base = `#${toHex(rgba.r)}${toHex(rgba.g)}${toHex(rgba.b)}`;
  if (allowAlpha && rgba.a < 1) {
    return `${base}${toHex(clamp(rgba.a, 0, 1) * 255)}`;
  }
  return base;
}

export function rgbToHsv(r: number, g: number, b: number): HSV {
  const rN = r / 255;
  const gN = g / 255;
  const bN = b / 255;
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rN) h = 60 * (((gN - bN) / d) % 6);
    else if (max === gN) h = 60 * ((bN - rN) / d + 2);
    else h = 60 * ((rN - gN) / d + 4);
  }
  if (h < 0) h += 360;
  const s = max === 0 ? 0 : (d / max) * 100;
  const v = max * 100;
  return { h, s, v };
}

export function hsvToRgb(h: number, s: number, v: number): { r: number; g: number; b: number } {
  const sN = clamp(s, 0, 100) / 100;
  const vN = clamp(v, 0, 100) / 100;
  const c = vN * sN;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hh < 1) [r1, g1, b1] = [c, x, 0];
  else if (hh < 2) [r1, g1, b1] = [x, c, 0];
  else if (hh < 3) [r1, g1, b1] = [0, c, x];
  else if (hh < 4) [r1, g1, b1] = [0, x, c];
  else if (hh < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = vN - c;
  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
}

type HsvaState = HSV & { a: number };

function hsvaToRgba(hsva: HsvaState): RGBA {
  const rgb = hsvToRgb(hsva.h, hsva.s, hsva.v);
  return { ...rgb, a: hsva.a };
}

/* ================================================================
 * Shared visual constants — the checkerboard alpha backdrop and hue
 * ramp render arbitrary/derived color *data*, not chrome, so per
 * DESIGN.md these may be computed/literal rather than tokens.
 * ================================================================ */

const CHECKERBOARD_STYLE: React.CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
  backgroundSize: '10px 10px',
  backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
  backgroundColor: '#fff',
};

const HUE_GRADIENT =
  'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)';

function ColorSwatch({ color, className }: { color: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-block overflow-hidden rounded-sm border border-border-strong',
        className
      )}
    >
      <span className="absolute inset-0" style={CHECKERBOARD_STYLE} />
      <span className="absolute inset-0" style={{ backgroundColor: color }} />
    </span>
  );
}

/* ================================================================
 * Saturation/Value 2D field
 * ================================================================ */

function SaturationValueField({
  id,
  hue,
  saturation,
  value,
  onChange,
}: {
  id: string;
  hue: number;
  saturation: number;
  value: number;
  onChange: (next: { s: number; v: number }) => void;
}) {
  const padRef = React.useRef<HTMLDivElement | null>(null);
  const draggingRef = React.useRef(false);

  const updateFromClient = React.useCallback(
    (clientX: number, clientY: number) => {
      const el = padRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const s = clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
      const v = clamp(100 - ((clientY - rect.top) / rect.height) * 100, 0, 100);
      onChange({ s, v });
    },
    [onChange]
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClient(e.clientX, e.clientY);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    updateFromClient(e.clientX, e.clientY);
  };
  const stopDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released */
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? 10 : 2;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onChange({ s: clamp(saturation - step, 0, 100), v: value });
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      onChange({ s: clamp(saturation + step, 0, 100), v: value });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      onChange({ s: saturation, v: clamp(value + step, 0, 100) });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      onChange({ s: saturation, v: clamp(value - step, 0, 100) });
    }
  };

  const pureHue = hsvToRgb(hue, 100, 100);

  return (
    <div
      ref={padRef}
      role="slider"
      tabIndex={0}
      id={id}
      aria-label="Color saturation and brightness"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value)}
      aria-valuetext={`Saturation ${Math.round(saturation)}%, brightness ${Math.round(value)}%`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={stopDrag}
      onPointerCancel={stopDrag}
      onKeyDown={handleKeyDown}
      className="relative h-40 w-full cursor-crosshair touch-none rounded-md border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      style={{
        backgroundColor: `rgb(${pureHue.r}, ${pureHue.g}, ${pureHue.b})`,
        backgroundImage:
          'linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)',
      }}
    >
      <span
        className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md ring-1 ring-black/40"
        style={{ left: `${saturation}%`, top: `${100 - value}%` }}
      />
    </div>
  );
}

/* ================================================================
 * 1D gradient slider — used for hue and alpha
 * ================================================================ */

function GradientSlider({
  id,
  label,
  value,
  min,
  max,
  trackStyle,
  onChange,
  valueText,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  trackStyle: React.CSSProperties;
  onChange: (value: number) => void;
  valueText?: string;
}) {
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const draggingRef = React.useRef(false);

  const updateFromClientX = React.useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
      onChange(min + ratio * (max - min));
    },
    [onChange, min, max]
  );

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    updateFromClientX(e.clientX);
  };
  const stopDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    draggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* pointer already released */
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? (max - min) / 10 : (max - min) / 100 || 1;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      onChange(clamp(value - step, min, max));
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      onChange(clamp(value + step, min, max));
    } else if (e.key === 'Home') {
      e.preventDefault();
      onChange(min);
    } else if (e.key === 'End') {
      e.preventDefault();
      onChange(max);
    }
  };

  const percent = ((value - min) / (max - min)) * 100;

  return (
    <div className="flex min-h-[32px] items-center">
      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        id={id}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={Math.round(value)}
        aria-valuetext={valueText}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDrag}
        onPointerCancel={stopDrag}
        onKeyDown={handleKeyDown}
        className="relative h-3.5 w-full cursor-pointer touch-none rounded-full border border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        style={trackStyle}
      >
        <span
          className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md ring-1 ring-black/30"
          style={{ left: `${percent}%` }}
        />
      </div>
    </div>
  );
}

/* ================================================================
 * ColorPicker — the inline panel
 * ================================================================ */

export interface ColorPickerProps {
  /** Any of #hex, #hex8, rgb(), rgba(). */
  value: string;
  onChange: (hex: string) => void;
  /** Default true. When false, the alpha slider/field are hidden and every emitted value is #rrggbb. */
  allowAlpha?: boolean;
  swatches?: string[];
  className?: string;
}

export const ColorPicker = React.forwardRef<HTMLDivElement, ColorPickerProps>(
  ({ value, onChange, allowAlpha = true, swatches, className }, ref) => {
    const uid = React.useId();
    const eyeDropperSupported = typeof window !== 'undefined' && 'EyeDropper' in window;

    const [hsva, setHsva] = React.useState<HsvaState>(() => {
      const rgba = parseColor(value) ?? { r: 0, g: 0, b: 0, a: 1 };
      const hsv = rgbToHsv(rgba.r, rgba.g, rgba.b);
      return { ...hsv, a: allowAlpha ? rgba.a : 1 };
    });

    // Sync from external `value` changes without fighting an in-progress
    // drag or clobbering hue when the color is grayscale (hue is otherwise
    // undefined at s=0, so we keep whatever hue the user last dragged to).
    React.useEffect(() => {
      const parsed = parseColor(value);
      if (!parsed) return;
      setHsva((prev) => {
        const prevRgba = hsvaToRgba(prev);
        if (
          formatHex(prevRgba, allowAlpha).toLowerCase() ===
          formatHex(parsed, allowAlpha).toLowerCase()
        ) {
          return prev;
        }
        const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
        const isGray = parsed.r === parsed.g && parsed.g === parsed.b;
        return { h: isGray ? prev.h : hsv.h, s: hsv.s, v: hsv.v, a: allowAlpha ? parsed.a : 1 };
      });
    }, [value, allowAlpha]);

    function commit(patch: Partial<HsvaState>) {
      const merged = { ...hsva, ...patch };
      setHsva(merged);
      onChange(formatHex(hsvaToRgba(merged), allowAlpha));
    }

    const rgb = hsvToRgb(hsva.h, hsva.s, hsva.v);
    const currentHex = formatHex(hsvaToRgba(hsva), allowAlpha);

    const [hexDraft, setHexDraft] = React.useState(() => currentHex.replace(/^#/, ''));
    const hexFocused = React.useRef(false);
    React.useEffect(() => {
      if (hexFocused.current) return;
      setHexDraft(currentHex.replace(/^#/, ''));
    }, [currentHex]);

    function handleHexChange(e: React.ChangeEvent<HTMLInputElement>) {
      const raw = e.target.value;
      setHexDraft(raw);
      const parsed = parseHex(raw);
      if (parsed) {
        const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
        const isGray = parsed.r === parsed.g && parsed.g === parsed.b;
        commit({
          h: isGray ? hsva.h : hsv.h,
          s: hsv.s,
          v: hsv.v,
          a: allowAlpha ? parsed.a : 1,
        });
      }
    }

    function handleChannelChange(channel: 'r' | 'g' | 'b', raw: string) {
      const n = clamp(Math.round(Number(raw)), 0, 255);
      if (Number.isNaN(n)) return;
      const nextRgb = { ...rgb, [channel]: n };
      const hsv = rgbToHsv(nextRgb.r, nextRgb.g, nextRgb.b);
      commit({ h: hsv.h, s: hsv.s, v: hsv.v });
    }

    function handleAlphaPercentChange(raw: string) {
      const n = clamp(Math.round(Number(raw)), 0, 100);
      if (Number.isNaN(n)) return;
      commit({ a: n / 100 });
    }

    async function handleEyeDropper() {
      const EyeDropperCtor = (
        window as unknown as { EyeDropper?: new () => { open: () => Promise<{ sRGBHex: string }> } }
      ).EyeDropper;
      if (!EyeDropperCtor) return;
      try {
        const instance = new EyeDropperCtor();
        const result = await instance.open();
        const parsed = parseHex(result.sRGBHex);
        if (parsed) {
          const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
          commit({ h: hsv.h, s: hsv.s, v: hsv.v, a: allowAlpha ? hsva.a : 1 });
        }
      } catch {
        // User cancelled (AbortError) or the API isn't allowed here — no-op.
      }
    }

    function handleSwatchPick(swatch: string) {
      const parsed = parseColor(swatch);
      if (!parsed) return;
      const hsv = rgbToHsv(parsed.r, parsed.g, parsed.b);
      commit({ h: hsv.h, s: hsv.s, v: hsv.v, a: allowAlpha ? parsed.a : 1 });
    }

    const previewCss = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${hsva.a})`;
    const alphaTrackStyle: React.CSSProperties = {
      backgroundImage: `${CHECKERBOARD_STYLE.backgroundImage as string}, linear-gradient(to right, rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0), rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1))`,
      backgroundSize: `${CHECKERBOARD_STYLE.backgroundSize as string}, ${CHECKERBOARD_STYLE.backgroundSize as string}, ${CHECKERBOARD_STYLE.backgroundSize as string}, ${CHECKERBOARD_STYLE.backgroundSize as string}, 100% 100%`,
      backgroundPosition: `${CHECKERBOARD_STYLE.backgroundPosition as string}, 0 0`,
      backgroundColor: CHECKERBOARD_STYLE.backgroundColor as string,
    };

    const channels: Array<{ key: 'r' | 'g' | 'b'; label: string }> = [
      { key: 'r', label: 'R' },
      { key: 'g', label: 'G' },
      { key: 'b', label: 'B' },
    ];

    return (
      <div
        ref={ref}
        role="group"
        aria-label="Color picker"
        className={cn(
          'w-[260px] rounded-lg border border-border bg-surface p-3 shadow-lg',
          className
        )}
      >
        <SaturationValueField
          id={`${uid}-sv`}
          hue={hsva.h}
          saturation={hsva.s}
          value={hsva.v}
          onChange={(next) => commit({ s: next.s, v: next.v })}
        />

        <div className="mt-3 space-y-1.5">
          <GradientSlider
            id={`${uid}-hue`}
            label="Hue"
            value={hsva.h}
            min={0}
            max={360}
            trackStyle={{ backgroundImage: HUE_GRADIENT }}
            onChange={(h) => commit({ h })}
            valueText={`${Math.round(hsva.h)} degrees`}
          />
          {allowAlpha && (
            <GradientSlider
              id={`${uid}-alpha`}
              label="Alpha"
              value={hsva.a * 100}
              min={0}
              max={100}
              trackStyle={alphaTrackStyle}
              onChange={(a) => commit({ a: a / 100 })}
              valueText={`${Math.round(hsva.a * 100)}%`}
            />
          )}
        </div>

        <div className="mt-3 flex items-end gap-2">
          <ColorSwatch color={previewCss} className="mb-[1px] h-8 w-8 shrink-0" />
          <div className="flex-1">
            <Label htmlFor={`${uid}-hex`} className="text-xs">
              Hex
            </Label>
            <div className="relative mt-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted">
                #
              </span>
              <input
                id={`${uid}-hex`}
                type="text"
                value={hexDraft}
                onFocus={() => {
                  hexFocused.current = true;
                }}
                onBlur={() => {
                  hexFocused.current = false;
                  setHexDraft(currentHex.replace(/^#/, ''));
                }}
                onChange={handleHexChange}
                spellCheck={false}
                className="h-8 w-full rounded-md border border-border bg-surface py-1 pl-6 pr-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              />
            </div>
          </div>
          {eyeDropperSupported && (
            <button
              type="button"
              aria-label="Pick color from screen"
              onClick={handleEyeDropper}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border-strong text-text transition-colors duration-fast hover:bg-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <Icon icon={Pipette} em={1.2308} />
            </button>
          )}
        </div>

        <div className={cn('mt-2 grid gap-2', allowAlpha ? 'grid-cols-4' : 'grid-cols-3')}>
          {channels.map(({ key, label }) => (
            <div key={key}>
              <Label htmlFor={`${uid}-${key}`} className="text-xs">
                {label}
              </Label>
              <input
                id={`${uid}-${key}`}
                type="number"
                min={0}
                max={255}
                value={Math.round(rgb[key])}
                onChange={(e) => handleChannelChange(key, e.target.value)}
                className="mt-1 h-8 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              />
            </div>
          ))}
          {allowAlpha && (
            <div>
              <Label htmlFor={`${uid}-a`} className="text-xs">
                A%
              </Label>
              <input
                id={`${uid}-a`}
                type="number"
                min={0}
                max={100}
                value={Math.round(hsva.a * 100)}
                onChange={(e) => handleAlphaPercentChange(e.target.value)}
                className="mt-1 h-8 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              />
            </div>
          )}
        </div>

        {swatches && swatches.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5 border-t border-border pt-3">
            {swatches.map((swatch, i) => {
              const parsed = parseColor(swatch);
              const isActive =
                !!parsed &&
                formatHex(parsed, allowAlpha).toLowerCase() === currentHex.toLowerCase();
              return (
                <button
                  key={`${swatch}-${i}`}
                  type="button"
                  aria-label={`Use color ${swatch}`}
                  aria-pressed={isActive}
                  onClick={() => handleSwatchPick(swatch)}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                    isActive ? 'ring-2 ring-primary' : 'hover:bg-surface-alt'
                  )}
                >
                  <ColorSwatch
                    color={parsed ? formatHex(parsed, true) : swatch}
                    className="h-5 w-5"
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);
ColorPicker.displayName = 'ColorPicker';

/* ================================================================
 * ColorPickerPopover — swatch-button trigger + portal panel
 * ================================================================ */

export interface ColorPickerPopoverProps extends ColorPickerProps {
  /** Accessible label for the trigger button. Default "Pick a color". */
  'aria-label'?: string;
  disabled?: boolean;
  triggerClassName?: string;
}

export const ColorPickerPopover = React.forwardRef<HTMLButtonElement, ColorPickerPopoverProps>(
  (
    {
      value,
      onChange,
      allowAlpha = true,
      swatches,
      className,
      triggerClassName,
      disabled,
      'aria-label': ariaLabel = 'Pick a color',
    },
    forwardedRef
  ) => {
    const [open, setOpen] = React.useState(false);
    const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null);
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);
    const panelRef = React.useRef<HTMLDivElement | null>(null);

    React.useImperativeHandle(forwardedRef, () => triggerRef.current as HTMLButtonElement);

    const close = React.useCallback(() => {
      setOpen(false);
      setCoords(null);
      triggerRef.current?.focus();
    }, []);

    const reposition = React.useCallback(() => {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const triggerRect = trigger.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const margin = 8;
      let top = triggerRect.bottom + margin;
      let left = triggerRect.left;
      if (left + panelRect.width > window.innerWidth - margin) {
        left = Math.max(margin, triggerRect.right - panelRect.width);
      }
      if (top + panelRect.height > window.innerHeight - margin) {
        top = Math.max(margin, triggerRect.top - panelRect.height - margin);
      }
      setCoords({ top, left });
    }, []);

    React.useLayoutEffect(() => {
      if (!open) return;
      reposition();
    }, [open, reposition]);

    React.useEffect(() => {
      if (!open) return undefined;
      const handleMouseDown = (e: MouseEvent) => {
        const target = e.target as Node;
        if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
        close();
      };
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      };
      const handleReposition = () => reposition();
      document.addEventListener('mousedown', handleMouseDown);
      document.addEventListener('keydown', handleKeyDown);
      window.addEventListener('resize', handleReposition);
      window.addEventListener('scroll', handleReposition, true);
      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
        document.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('resize', handleReposition);
        window.removeEventListener('scroll', handleReposition, true);
      };
    }, [open, close, reposition]);

    React.useEffect(() => {
      if (!open) return undefined;
      const raf = requestAnimationFrame(() => {
        const focusable = panelRef.current?.querySelector<HTMLElement>(
          '[role="slider"], input, button'
        );
        focusable?.focus();
      });
      return () => cancelAnimationFrame(raf);
    }, [open]);

    const rgba = parseColor(value) ?? { r: 0, g: 0, b: 0, a: 1 };
    const swatchCss = `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;

    return (
      <>
        <button
          ref={triggerRef}
          type="button"
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-md border border-border-strong p-1 transition-colors duration-fast hover:bg-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            triggerClassName
          )}
        >
          <ColorSwatch color={swatchCss} className="h-full w-full" />
        </button>
        {open &&
          createPortal(
            <div
              ref={panelRef}
              role="dialog"
              aria-label={ariaLabel}
              // Marks the portal'd panel so host surfaces with their own
              // mousedown-outside dismissal (context menus etc.) can exempt
              // clicks inside the picker — the panel lives on document.body,
              // outside any host's DOM subtree.
              data-colorpicker-panel
              {...appLayerProps}
              style={{
                position: 'fixed',
                top: coords?.top ?? 0,
                left: coords?.left ?? 0,
                visibility: coords ? 'visible' : 'hidden',
                // Must clear a Dialog when opened from inside one (dialog.tsx
                // exempts this panel from outside-click dismissal for the
                // same reason) — same layer as ContextMenu/Tooltip.
                zIndex: 'var(--z-popover)',
              }}
              className="motion-safe:animate-fade-in"
            >
              <ColorPicker
                value={value}
                onChange={onChange}
                allowAlpha={allowAlpha}
                swatches={swatches}
                className={className}
              />
            </div>,
            document.body
          )}
      </>
    );
  }
);
ColorPickerPopover.displayName = 'ColorPickerPopover';
